import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Input, Upload, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faPlus } from "@fortawesome/free-solid-svg-icons";
import { getUserDetailsFromReducer, updateUserDetailsInReducer } from "Store/reducers/Auth/AuthSlice";
import { uploadProfilePic } from "Utility/UploadFile";
import UploadProfilePic from "@/components/UploadProfilePic";
import { updateAccountDetails } from "Sdk/Actions";

// Remove bottom padding from AntD form item labels in this component
const style = `
:where(.ant-form-vertical) .ant-form-item:not(.ant-form-item-horizontal) .ant-form-item-label {
  padding-bottom: 0 !important;
}
`;

// Debounce function to limit the rate of updates
const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

// Define debounced function ONCE (no useCallback needed)
const debouncedUpdateAccountDetails = debounce(async (updatedData) => {
  await updateAccountDetails(updatedData);
  message.success("Changes saved! Publish to go live.");
}, 3000);

export const ActionBarBasicTab = () => {
  const userDetails = useSelector(getUserDetailsFromReducer);
  const dispatch = useDispatch();
  const [logoS3Key, setLogoS3Key] = useState(userDetails?.logo || null);
  const [imagePreview, setImagePreview] = useState(null);
  const [form] = Form.useForm();

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleLogoChange = async (info) => {
    const file = info?.file?.originFileObj || info?.file;
    if (!file) return;

    setImagePreview(await getBase64(file));

    try {
      const uploadResult = await uploadProfilePic(file, userDetails?.username);
      const newLogoKey = uploadResult?.key;
      setLogoS3Key(newLogoKey);

      // Immediate Redux update
      dispatch(updateUserDetailsInReducer({ logo: newLogoKey }));

      // Debounced database update
      debouncedUpdateAccountDetails({
        logo: newLogoKey,
        username: userDetails?.username,
      });
    } catch (e) {
      message.error("Failed to upload logo");
    }
  };

  const handleFieldChange = (field, value) => {
    const updatedData = {
      [field]: value,
      username: userDetails?.username,
    };
    // Immediate Redux update
    dispatch(updateUserDetailsInReducer({ [field]: value }));
    // Debounced database update
    debouncedUpdateAccountDetails(updatedData);
  };

  // Set initial form values from userDetails
  useEffect(() => {
    form.setFieldsValue({
      name: userDetails?.name || "",
      description: userDetails?.description || "",
      phone: userDetails?.phone || "",
      email: userDetails?.email || "",
    });
    setLogoS3Key(userDetails?.logo || null);
  }, [userDetails, form]);

  return (
    <>
      <style>{style}</style>
      <div className="h-full overflow-y-auto">
        <Form layout="vertical" form={form}>
          <div className="flex items-center mb-2">
            <FontAwesomeIcon icon={faUser} className="mr-2 text-xs" />
            <span className="font-semibold text-sm">Business Information</span>
          </div>

          {/* Removed name="photoUrl" to avoid Upload warning */}
          <Form.Item className="mb-2" label={<span className="text-xs mb-1 block font-semibold">Business Logo</span>}>
            <Upload.Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleLogoChange}
              className="flex items-center border-dashed"
              style={{ borderRadius: "50%", width: 92, height: 92 }}
            >
              {logoS3Key || imagePreview ? (
                <UploadProfilePic
                  S3Key={logoS3Key}
                  src={imagePreview}
                  alt="Business Logo"
                  className="rounded-full object-cover"
                  width={92}
                  height={92}
                />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <FontAwesomeIcon icon={faPlus} className="text-gray-400 text-lg" />
                  <div className="text-xs mt-1">Add Logo</div>
                </div>
              )}
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            className="mb-2"
            label={<span className="text-xs mb-1 block font-semibold">Business Name</span>}
            name="name"
            rules={[{ required: true, message: "Please enter business name" }]}
          >
            <Input
              className="input-border text-xs"
              placeholder="e.g. Acme Corporation"
              onChange={(e) => handleFieldChange("name", e.target.value)}
            />
          </Form.Item>

          <Form.Item
            className="mb-2"
            label={<span className="text-xs mb-1 block font-semibold">Description</span>}
            name="description"
          >
            <Input.TextArea
              className="input-border text-xs p-2"
              placeholder="Describe your business, services, or tagline"
              autoSize
              onChange={(e) => handleFieldChange("description", e.target.value)}
            />
          </Form.Item>

          <Form.Item
            className="mb-2"
            label={<span className="text-xs mb-1 block font-semibold">Phone</span>}
            name="phone"
          >
            <Input
              className="input-border text-xs"
              placeholder="e.g. +1 (555) 123-4567"
              onChange={(e) => handleFieldChange("phone", e.target.value)}
            />
          </Form.Item>

          <Form.Item
            className="mb-2"
            label={<span className="text-xs mb-1 block font-semibold">Email</span>}
            name="email"
          >
            <Input
              className="input-border text-xs"
              placeholder="e.g. info@acmecorp.com"
              onChange={(e) => handleFieldChange("email", e.target.value)}
            />
          </Form.Item>
        </Form>
      </div>
    </>
  );
};
